<template>
	<div class="table-field-settings field-settings needs-data-source" :class="{ 'has-data-source': selectedNodeUUID }" v-if="localField">
		<v-expansion-panels v-model="panel" flat accordion>
			<ExpansionPanel :title="'Basics'">
				<SettingSwitch
					:title="'Label'"
					:placeholder="'Type your title here'"
					:switchValue.sync="titleEnabled"
					:textFieldValue.sync="titleValue"
				/>
			</ExpansionPanel>
			<ExpansionPanel :title="'Data'">
				<ol class="field-setting-section">
					<li class="configuration-step choose-data-source">
						<div class="step-title">Choose data source</div>
						<CombinedInputFieldDataMenu
							:value.sync="selectedSource"
							readonly
							hasDelete
							:textAreaMode="false"
							:attachTo="'.choose-data-source'"
							:multipleSelection="false"
							:valuesTypes="dataInputMenuValueTypes"
						/>
						<div class="warning-message" v-if="!selectedNodeHasTestValues">
							You need to test the Data Source in order to fill the placements
						</div>
					</li>
					<li class="configuration-step">
						<div class="step-title">Choose Headers</div>
						<HeaderSelector :headers="tableHeaders" :selectedHeaders.sync="selectedHeaders" :multipleSelect="true" />
					</li>
					<li class="configuration-step">
						<div class="step-title">Column order</div>
						<AppSelect bottom class="mr-1" outlined dense v-model="columnOrder" :items="columnOrderItems" />
					</li>
				</ol>
			</ExpansionPanel>
			<ExpansionPanel :title="'Controls'">
				<div class="step-title mb-6">User controls</div>
				<Row class="width-100 flex-align-center flex-justify-space-between mb-3">
					<h3 class="reg">Default page size</h3>
					<AppSelect bottom class="page-size-selector mr-1" outlined dense v-model="pageSize" :items="pageSizeItems" />
				</Row>
				<SwitchOption option-name="Allow editing data" :switch-model.sync="editableEnabled" />
				<SwitchOption option-name="Allow adding rows" v-if="editableEnabled" :switch-model.sync="allowAddingRows" />
				<SwitchOption option-name="Find" :switch-model.sync="findEnabled" />
				<SwitchOption option-name="Select" :switch-model.sync="userSelection" />
				<div class="selection-sub-folder" v-if="userSelection">
					<SwitchOption option-name="Multiple" :switch-model.sync="multipleSelection" />
					<SwitchOption option-name="Required" :switch-model.sync="requiredEnabled" />
				</div>
			</ExpansionPanel>
			<ExpansionPanel :title="'Explanations'">
				<SettingSwitch
					:title="'Description'"
					:placeholder="'Type your description here'"
					:switchValue.sync="descriptionEnabled"
					:textFieldValue.sync="descriptionValue"
				/>
			</ExpansionPanel>
		</v-expansion-panels>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

import HeaderSelector from './HeaderSelector';
import { DEFAULT_SELECTED_HEADERS_LOWERCASED } from './headers.const';
import TableRelatedFieldSettingsMixin from '../TableRelatedFieldSettingsMixin';
import TableOnlyAllowedValueTypesMixin from '../ValueTypesMixins/TableOnlyAllowedValueTypesMixin';
import ExpansionPanel from '../WidgetSettings/ExpansionPanel';
import SettingSwitch from '../WidgetSettings/SettingSwitch';
import TableActionEditorService from '@/components/BlockEditor/TableActionEditor/table-action-edtior-service';
import FieldSettingsMixin from '@/components/BlockEditorWidget/FieldSettings/FieldSettingsMixin';
import CombinedInputFieldDataMenu from '@/components/DataInput/CombinedInputFieldDataMenu';
import { getPreviousStepDataForNodeByUUID } from '@/store/modules/nodes/getters';
import { nodesDataByUUID, replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import ParamsService from '@/utils/ParamsService';
import { flattenPreserveArrays } from '@/utils/helpers';

export default {
	name: 'TableFieldSettings',
	components: {
		SettingSwitch,
		ExpansionPanel,
		HeaderSelector,
		CombinedInputFieldDataMenu,
	},
	mixins: [FieldSettingsMixin, TableOnlyAllowedValueTypesMixin, TableRelatedFieldSettingsMixin],
	computed: {
		...mapGetters([
			nodesDataByUUID.fullPath,
			getPreviousStepDataForNodeByUUID.fullPath,
			replaceInsertDataStringDictionary.fullPath,
			configurationsMappingBlocksUUIDs.fullPath,
		]),
		selectedNodeUUID() {
			return ParamsService.extractNodeUUIDFromValue(this.selectedSource);
		},
		selectedPath() {
			return ParamsService.extractPathFromValue(
				this.selectedSource,
				this[replaceInsertDataStringDictionary.fullPath],
				this[configurationsMappingBlocksUUIDs.fullPath]
			);
		},
		selectedSource: {
			get() {
				return this.localField.metadata.options.step_id.value;
			},
			set(value) {
				const hasChanged = this.selectedSource !== value;
				this.setLocalFieldKey('metadata.options.step_id', { value });
				if (hasChanged) {
					this.initSelectedHeaders(); // reset the selected headers when changing selected node
				}
			},
		},
		selectedHeaders: {
			get() {
				return this.localField.metadata.options.headers || [];
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.headers', value);
			},
		},
		selectedNodeData() {
			return this[nodesDataByUUID.fullPath][this.selectedNodeUUID];
		},
		selectedNodePreviousStepsData() {
			return this[getPreviousStepDataForNodeByUUID.fullPath](this.selectedNodeUUID) || {};
		},
		selectedNodeHasTestValues() {
			return Object.keys(this.selectedNodeData ? this.selectedNodeData.test_result || {} : {}).length > 0;
		},
		userSelection: {
			get() {
				return this.localField.metadata.options.user_selection.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.enabled', value);
				if (!value) {
					this.multipleSelection = false;
					this.requiredEnabled = false;
				}
			},
		},
		multipleSelection: {
			get() {
				return this.localField.metadata.options.user_selection.multiple;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.multiple', value);
			},
		},
		tableHeaders() {
			return Object.keys(flattenPreserveArrays(this.exampleRow(this.selectedNodePreviousStepsData, this.selectedPath)));
		},
		editableEnabled: {
			get() {
				return this.localField.metadata.options.editable.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.editable.enabled', value);
			},
		},
		pageSize: {
			get() {
				return this.localField.metadata.options.page_size.value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.page_size.value', value);
			},
		},
		columnOrder: {
			get() {
				return this.localField.metadata.options.column_order.value;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.column_order.value', value);
			},
		},
		allowAddingRows: {
			get() {
				return this.localField.metadata.options.add_rows.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.add_rows.enabled', value);
			},
		},
		findEnabled: {
			get() {
				return this.localField.metadata.options.find.enabled;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.find.enabled', value);
			},
		},
	},
	methods: {
		initSelectedHeaders(setDefaults = true) {
			if (setDefaults) {
				this.selectedHeaders = this.tableHeaders.reduce((headers, tableHeader) => {
					if (DEFAULT_SELECTED_HEADERS_LOWERCASED.indexOf(tableHeader.toLowerCase()) > -1) {
						headers.push(tableHeader);
					}
					return headers;
				}, []);
			} else {
				this.selectedHeaders = [];
			}
		},
	},
	data() {
		return {
			currentlyEdited: { currentEditedPlacement: null },
			pageSizeItems: TableActionEditorService.pageSizeItems,
			columnOrderItems: TableActionEditorService.columnOrderItems,
		};
	},
	mounted() {},
};
</script>

<style scoped lang="scss">
.page-size-selector {
	$margin: 16px;
	$h: 28px;

	height: calc(#{$h} - 4px);
	width: 50px;
	margin: 0 $margin;

	::v-deep(.v-select) {
		fieldset {
			height: $h;
			border-radius: 2px;
			border-color: #dcdfe6;
		}
		.v-input__append-inner {
			width: 16px;
			margin: 0;
		}
		.v-select__selection {
			min-width: 40px;
		}
		.v-select__selections .v-input__append-inner,
		.v-select__slot {
			height: $h;
		}
		.v-input__slot {
			min-height: $h;
		}
		.v-icon::before {
			font-size: 12px !important;
			color: #606266;
			margin-top: -14px;
		}
		.v-select__selection--comma {
			margin: -4px 0 0 0;
		}
		input {
			display: none;
		}
	}
}
</style>
